import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { languages, levels } from '../data/mock';
import { useLanguage } from '../contexts/LanguageContext';
import { BookOpen, Star, Users, Award, TrendingUp, Clock } from 'lucide-react';

const HomePage = () => {
  const navigate = useNavigate();
  const { currentLanguage, setCurrentLanguage, setCurrentLevel } = useLanguage();
  const [selectedLang, setSelectedLang] = useState(currentLanguage);

  const handleLanguageSelect = (langCode) => {
    setSelectedLang(langCode);
    setCurrentLanguage(langCode);
  };

  const handleGetStarted = () => {
    navigate('/auth');
  };

  const handleLevelSelect = (levelCode) => {
    setCurrentLevel(levelCode);
    navigate('/stories');
  };

  const handleLifetimePromo = () => {
    navigate('/pricing');
  };

  const handleGrammatik = () => {
    navigate('/grammar');
  };

  const handleBlog = () => {
    navigate('/blog');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white to-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <BookOpen className="h-8 w-8 text-[#4ECDC4]" />
              <span className="text-2xl font-bold text-slate-900">DMV-Lernportal</span>
            </div>
            <nav className="hidden md:flex items-center space-x-8">
              <Badge 
                variant="secondary" 
                className="bg-[#4ECDC4] text-white hover:bg-[#45b7aa] cursor-pointer"
                onClick={handleLifetimePromo}
              >
                Lifetime Promo
              </Badge>
              <Button variant="ghost" onClick={handleGrammatik}>Grammatik</Button>
              <Button variant="ghost" onClick={handleBlog}>Blog</Button>
              <Button variant="ghost">Deutsch ▼</Button>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <h1 className="text-5xl font-bold text-slate-900 leading-tight">
                  Lerne Sprachen mit{' '}
                  <span className="text-[#4ECDC4]">Storys</span> &{' '}
                  <span className="text-[#4ECDC4]">Nachrichten</span>
                </h1>
                <p className="text-xl text-slate-600 leading-relaxed">
                  DMV-Lernportal verwandelt spannende Storys in Mini-Sprachkurse für 
                  Beginner und Fortgeschrittene. Niveau A1-C1.
                </p>
              </div>

              {/* Language Selection */}
              <div className="space-y-4">
                <p className="text-lg font-medium text-slate-900">
                  ICH MÖCHTE{' '}
                  <span className="text-[#4ECDC4] font-bold">
                    {languages.find(l => l.code === selectedLang)?.name.toUpperCase()}
                  </span>{' '}
                  LERNEN
                </p>
                
                <div className="flex flex-wrap gap-3">
                  {languages.map((lang) => (
                    <Button
                      key={lang.code}
                      variant={selectedLang === lang.code ? "default" : "outline"}
                      className={`h-12 px-6 ${
                        selectedLang === lang.code 
                          ? 'bg-[#4ECDC4] hover:bg-[#45b7aa] text-white border-[#4ECDC4]' 
                          : 'border-slate-300 hover:border-[#4ECDC4] hover:text-[#4ECDC4]'
                      }`}
                      onClick={() => handleLanguageSelect(lang.code)}
                    >
                      <span className="text-lg mr-2">{lang.flag}</span>
                      {lang.name}
                    </Button>
                  ))}
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  className="bg-[#4ECDC4] hover:bg-[#45b7aa] text-white h-12 px-8 text-lg font-medium"
                  onClick={handleGetStarted}
                >
                  Kostenlos testen
                </Button>
                <Button 
                  variant="outline" 
                  className="h-12 px-8 text-lg"
                  onClick={() => navigate('/auth')}
                >
                  Ich habe ein Konto
                </Button>
              </div>
            </div>

            {/* Phone Mockups */}
            <div className="relative">
              <div className="flex justify-center items-center space-x-4">
                <div className="relative">
                  <div className="w-64 h-[500px] bg-slate-900 rounded-[3rem] p-2 shadow-2xl">
                    <div className="w-full h-full bg-white rounded-[2.5rem] overflow-hidden">
                      <div className="h-full bg-gradient-to-b from-slate-50 to-white p-4">
                        <div className="text-center mb-4">
                          <div className="text-sm font-medium text-slate-900">Discover DMV</div>
                        </div>
                        <div className="space-y-2 mb-4">
                          {levels.map((level, i) => (
                            <div key={level.code} className={`text-xs px-2 py-1 rounded ${i === 0 ? 'bg-[#4ECDC4] text-white' : 'bg-slate-100'}`}>
                              {level.code}
                            </div>
                          ))}
                        </div>
                        <div className="space-y-3">
                          <div className="bg-white rounded-lg p-3 shadow-sm">
                            <div className="w-full h-20 bg-slate-200 rounded mb-2"></div>
                            <div className="text-xs font-medium">Story Preview</div>
                            <div className="text-xs text-slate-600 mt-1">A1 • 3 Min</div>
                          </div>
                          <div className="bg-white rounded-lg p-3 shadow-sm">
                            <div className="w-full h-20 bg-slate-200 rounded mb-2"></div>
                            <div className="text-xs font-medium">Another Story</div>
                            <div className="text-xs text-slate-600 mt-1">B1 • 5 Min</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Language and Level Selection */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Wähle dein Sprachlevel</h2>
            <p className="text-lg text-slate-600">Beginne mit dem Level, das zu deinen Kenntnissen passt</p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-12">
            {levels.map((level) => (
              <Card 
                key={level.code} 
                className="cursor-pointer border-2 hover:border-[#4ECDC4] transition-all hover:shadow-lg"
                onClick={() => handleLevelSelect(level.code)}
              >
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold text-[#4ECDC4] mb-2">{level.code}</div>
                  <div className="text-sm text-slate-600">{level.name}</div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center">
            <p className="text-sm text-slate-500 mb-4">
              Nicht sicher welches Level? Starte mit A1 oder mach einen kurzen Test
            </p>
            <div className="flex justify-center space-x-4">
              <Button 
                variant="outline"
                onClick={() => handleLevelSelect('A1')}
                className="border-[#4ECDC4] text-[#4ECDC4] hover:bg-[#4ECDC4] hover:text-white"
              >
                Mit A1 starten
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate('/auth')}
              >
                Level-Test machen
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="flex items-center justify-center mb-4">
                <Star className="h-8 w-8 text-[#4ECDC4]" />
              </div>
              <div className="text-3xl font-bold text-slate-900">4,8</div>
              <div className="text-slate-600">BEWERTUNG</div>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center mb-4">
                <Users className="h-8 w-8 text-[#4ECDC4]" />
              </div>
              <div className="text-3xl font-bold text-slate-900">1 MILLION</div>
              <div className="text-slate-600">DOWNLOADS</div>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center mb-4">
                <Award className="h-8 w-8 text-[#4ECDC4]" />
              </div>
              <div className="text-3xl font-bold text-slate-900">WISSENSCHAFTLICH</div>
              <div className="text-slate-600">GEPRÜFT</div>
            </div>
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              Erfahre echte Immersion mit tausenden von Storys
            </h2>
            <p className="text-xl text-slate-600">
              DMV-Lernportal bringt deinen Wortschatz auf ein neues Level. 
              Verdreifache dein Vokabular in wenigen Wochen.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="text-center p-6 border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="space-y-4">
                <div className="w-16 h-16 bg-[#4ECDC4] rounded-full flex items-center justify-center mx-auto mb-4">
                  <BookOpen className="h-8 w-8 text-white" />
                </div>
                <h3 className="font-bold text-slate-900">Wähle</h3>
                <p className="text-slate-600">eine Story auf deinem Niveau aus.</p>
              </CardContent>
            </Card>

            <Card className="text-center p-6 border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="space-y-4">
                <div className="w-16 h-16 bg-[#4ECDC4] rounded-full flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="h-8 w-8 text-white" />
                </div>
                <h3 className="font-bold text-slate-900">Fokussiere</h3>
                <p className="text-slate-600">dich auf Wörter und Phrasen, die du lernen willst.</p>
              </CardContent>
            </Card>

            <Card className="text-center p-6 border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="space-y-4">
                <div className="w-16 h-16 bg-[#4ECDC4] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="h-8 w-8 text-white" />
                </div>
                <h3 className="font-bold text-slate-900">Trainiere</h3>
                <p className="text-slate-600">deine Auswahl mit smarten Karteikarten.</p>
              </CardContent>
            </Card>

            <Card className="text-center p-6 border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="space-y-4">
                <div className="w-16 h-16 bg-[#4ECDC4] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="h-8 w-8 text-white" />
                </div>
                <h3 className="font-bold text-slate-900">Meistere</h3>
                <p className="text-slate-600">jedes Niveau von A1 bis C1.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Level Selection */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Wähle dein Sprachniveau
            </h2>
            <p className="text-lg text-slate-600">
              Von Anfänger bis Fortgeschritten - A1 bis C1
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {levels.map((level) => (
              <Card 
                key={level.code}
                className="cursor-pointer hover:shadow-lg transition-all hover:scale-105 border-2 hover:border-[#4ECDC4]"
                onClick={() => handleLevelSelect(level.code)}
              >
                <CardContent className="text-center p-6">
                  <div className="text-2xl font-bold text-[#4ECDC4] mb-2">
                    {level.code}
                  </div>
                  <div className="font-medium text-slate-900 mb-1">
                    {level.name}
                  </div>
                  <div className="text-sm text-slate-600">
                    {level.description}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="py-20 bg-[#4ECDC4]">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-white mb-4">
            Folge über 1 Million Lernern und meistere Sprachen mit Storys
          </h2>
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            {languages.map((lang) => (
              <span key={lang.code} className="text-white text-lg">
                {lang.name}
              </span>
            ))}
          </div>
          <Button 
            className="bg-white text-[#4ECDC4] hover:bg-slate-100 h-12 px-8 text-lg font-medium"
            onClick={handleGetStarted}
          >
            Kostenlos testen
          </Button>
        </div>
      </section>
    </div>
  );
};

export default HomePage;